import React from "react";
import { size, transform } from "lodash";

import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import { FiChevronLeft } from "react-icons/fi";

/** One link request belonging to the source group's roster. */
interface AssociatedLinkRequest {
  employeeId: string;
  [key: string]: unknown;
}

/** Source group, derived from the original `PropTypes.obj` (a typo for
 *  `.object` — a runtime no-op under React 19); typed from render/handler usage. */
interface SourceGroup {
  name?: string;
  associatedLinkRequests: AssociatedLinkRequest[];
}

interface EmployeeSelectionProps {
  // injected by the react-step-wizard parent (StepWizard), not passed in the
  // parent's JSX — optional, invoked unconditionally with a non-null assertion
  goToNamedStep?: (stepName: string) => void;
  // identifier read by the react-step-wizard parent
  stepName?: string;
  push?: (...args: unknown[]) => void;
  setSourceGroupId?: (...args: unknown[]) => void;
  setTargetGroupId?: (...args: unknown[]) => void;
  isFetching?: boolean;
  client?: Record<string, unknown>;
  // declared `.isRequired` in propTypes but never passed by the parent and
  // unused in the body (a propTypes-vs-reality mismatch) — optional
  groups?: Record<string, unknown>;
  sourceGroupId?: string;
  targetGroupId?: string;
  onCancel?: (...args: unknown[]) => void;
  // Always dereferenced unconditionally (`sourceGroup.associatedLinkRequests`,
  // `sourceGroup.name`), so typed as required to match usage.
  sourceGroup: SourceGroup;
  employeesForMigration?: string[];
  setEmployeesForMigration: (employees: string[]) => void;
}

interface EmployeeSelectionState {
  selectAllEmployees: boolean;
  selectedEmployeeRows: Record<string, boolean>;
  // Pre-existing quirk: render() reads `this.state.initialFetching` but it is
  // never set in the initial state or any setState, so it is always undefined.
  initialFetching?: boolean;
}

class EmployeeSelection extends React.PureComponent<
  EmployeeSelectionProps,
  EmployeeSelectionState
> {
  constructor(props: EmployeeSelectionProps) {
    super(props);

    const selectedEmployeeRows = transform(
      // employeesForMigration is optional (PropTypes.array, not required) but
      // lodash's transform tolerates undefined at runtime (returns the
      // accumulator). Cast for the type signature only — no value/behavior
      // change.
      props.employeesForMigration as string[],
      (acc: Record<string, boolean>, val: string) => {
        acc[val] = true;
      },
      {}
    );
    this.state = {
      selectAllEmployees: false,
      selectedEmployeeRows,
    };
  }

  _buildEmployeeColumns = () => {
    const columns = [
      {
        label: "First Name",
        key: "userProfile.firstName",
      },
      {
        label: "Last Name",
        key: "userProfile.lastName",
      },
      {
        label: "Email",
        key: "userProfile.email",
      },

      {
        label: "Last 4 SSN",
        key: "userProfile.lastFourOfSsn",
      },
      {
        label: "Link Status",
        key: "status",
      },
    ];
    return columns;
  };

  _selectAllEmployees = (checked: boolean) => {
    const selectedEmployeeRows: Record<string, boolean> = {};
    this.props.sourceGroup.associatedLinkRequests.forEach((employee) => {
      selectedEmployeeRows[employee.employeeId] = checked;
    });
    this.setState({
      selectAllEmployees: checked,
      selectedEmployeeRows,
    });
  };

  _selectEmployee = (id: string, checked: boolean) => {
    const selectedEmployeeRows = {
      ...this.state.selectedEmployeeRows,
      [id]: checked,
    };

    const selectAllEmployees =
      !checked && this.state.selectAllEmployees
        ? false
        : this.state.selectAllEmployees;

    this.setState({
      selectedEmployeeRows,
      selectAllEmployees,
    });
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const numOfEmployees = size(this.props.sourceGroup.associatedLinkRequests);
    const columns = this._buildEmployeeColumns();
    const data = this.props.sourceGroup.associatedLinkRequests;

    return (
      <div>
        <div className="workflow-widget">
          <div className="action-header">
            <p className="group-info">
              <span className="group-name">
                {this.props.sourceGroup.name}:{" "}
                <span className="invite-number">
                  Employees in source group (
                  <span className="number">{numOfEmployees}</span>)
                </span>
              </span>
            </p>
          </div>
          <IconTable
            columns={columns}
            data={data}
            hasSelectableRows
            uniqueRowIdentifier="employeeId"
            onSelectAll={(e) => this._selectAllEmployees(e.target.checked)}
            hasSelectedAll={this.state.selectAllEmployees}
            onRowSelect={(id, checked) => {
              this._selectEmployee(id, checked);
            }}
            selectedRows={this.state.selectedEmployeeRows}
          />

          <div className="process-payroll-button-row">
            <Button
              icon={{
                icon: <FiChevronLeft size={14} stroke={"#0cb7c4"} />,
                position: "left",
              }}
              onClick={() => this.props.goToNamedStep!("selectGroups")}
              name="cancel"
              btnLabel="Go Back"
              color="cancel"
            />
            <Button
              name="action"
              btnLabel={"Add Employees"}
              disabled={!size(this.state.selectedEmployeeRows)}
              onClick={() => {
                const selectedEmployees = transform(
                  this.state.selectedEmployeeRows,
                  (acc: string[], val, key) => {
                    if (val) {
                      acc.push(key);
                    }
                  },
                  []
                );

                this.props.setEmployeesForMigration(selectedEmployees);
                this.props.goToNamedStep!("summary");
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default EmployeeSelection;
